import * as React from 'react'; // eslint-disable-line
import styled, { ThemeProvider, ThemeProps } from 'styled-components';

import { COLOR, FONT_SIZE, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';
import SortNumberAsc from '../../packages/icons/sort-number-asc';
import SortNumberDesc from '../../packages/icons/sort-number-desc';

export interface SortNumberAscDescFieldProps {
    dataId: string;
    id: string;
    name: string;
    legend: string;
    autoFocus?: boolean;
    assistAccess?: boolean;
    value?: SortValues;
    disabled?: boolean;
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    invalid?: boolean;
    thinLabel?: boolean;
    thinLegend?: boolean;
    theme?: SortFieldTheme;
    onChange?: (e: any) => void;
    validationMessage?: string;
    className?: string;
}

export enum SortValues {
    NONE = 'NONE',
    ASCENDING = 'ASC',
    DESCENDING = 'DESC',
}

interface SortFieldTheme {
    selectedColor: string;
}

interface StyledLegendTextProps {
    disabled?: boolean;
    invalid?: boolean;
    thinLegend?: boolean;
}

interface StyledLabelProps {
    inline?: boolean;
    disabled?: boolean;
}

interface StyledRadioProps {
    assistAccess: boolean;
    disabled?: boolean;
}

interface StyledLabelTextProps {
    disabled?: boolean;
    inline?: boolean;
    thinLabel?: boolean;
}

const Wrapper = styled.fieldset`
    display: inline-flex;
    flex-direction: column;
`;

const WrapperHasHelper = styled.fieldset`
    display: inline-flex;
    flex-direction: column;
`;

const StyledLegend = styled.legend`
    display: inline-flex;
    margin-left: 0;
`;

const StyledLegendText = styled.span<StyledLegendTextProps>`
    display: inline-block;
    margin-right: ${SPACING.X_SMALL};
    padding-bottom: ${SPACING.XX_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledLegendTextProps): string => (props.thinLegend ? '400' : '700')};
    color: ${(props: StyledLegendTextProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledLegendTextProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledLabel = styled.label<StyledLabelProps>`
    position: relative;
    display: inline-flex;
    flex-direction: ${(props: StyledLabelProps): string => (props.inline ? 'row-reverse' : 'column')};
    align-items: ${(props: StyledLabelProps): string => (props.inline ? 'center' : '')};
    justify-content: ${(props: StyledLabelProps): string => (props.inline ? 'flex-end' : '')};
    cursor: ${(props: StyledLabelProps): string => (props.disabled ? 'default' : 'pointer')};
`;

const StyledLabelText = styled.span<StyledLabelTextProps>`
    display: block;
    margin-left: ${(props: StyledLabelTextProps): string => (props.inline ? SPACING.SMALL : '0')};
    font-size: ${FONT_SIZE.SMALL};
    font-weight: 400;
    color: ${COLOR.MAIN.LABEL_TEXT};
    line-height: ${FONT_SIZE.MEDIUM};
    opacity: ${(props: StyledLabelTextProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledItemsWrapper = styled.div`
    > ${StyledLabel} {
        margin-right: ${SPACING.LARGE};
    }
`;

const StyledAscIcon = styled(SortNumberAsc)`
    width: 30px;
    height: 30px;
    user-select: none;
`;

const StyledDescIcon = styled(SortNumberDesc)`
    width: 30px;
    height: 30px;
    user-select: none;
`;

const StyledRadio = styled.input<StyledRadioProps>`
    /* NOTE: it's important the radio position on screen matches where the label is or the browser may scroll to the radio */
    position: absolute;
    opacity: 0;
    top: 0;
    left: 0;
    z-index: -1;

    ~ ${StyledAscIcon} .sort-number-asc-icon__background,
    ~ ${StyledDescIcon} .sort-number-desc-icon__background {
        fill: #fff;
    }
    ~ ${StyledAscIcon} .sort-number-asc-icon__outline,
    ~ ${StyledAscIcon} .sort-number-asc-icon__1,
    ~ ${StyledAscIcon} .sort-number-asc-icon__9,
    ~ ${StyledAscIcon} .sort-number-asc-icon__arrow,
    ~ ${StyledDescIcon} .sort-number-desc-icon__outline,
    ~ ${StyledDescIcon} .sort-number-desc-icon__1,
    ~ ${StyledDescIcon} .sort-number-desc-icon__9,
    ~ ${StyledDescIcon} .sort-number-desc-icon__arrow {
        fill: #d5d5d5;
    }

    &:hover {
        ~ ${StyledAscIcon} .sort-number-asc-icon__outline,
        ~ ${StyledAscIcon} .sort-number-asc-icon__1,
        ~ ${StyledAscIcon} .sort-number-asc-icon__9,
        ~ ${StyledAscIcon} .sort-number-asc-icon__arrow,
        ~ ${StyledDescIcon} .sort-number-desc-icon__outline,
        ~ ${StyledDescIcon} .sort-number-desc-icon__1,
        ~ ${StyledDescIcon} .sort-number-desc-icon__9,
        ~ ${StyledDescIcon} .sort-number-desc-icon__arrow {
            fill: ${(props: StyledRadioProps): string => (props.disabled ? '#d5d5d5' : '#9a9a9a')};
        }
    }

    &:focus ~ ${StyledAscIcon}, &:focus ~ ${StyledDescIcon} {
        outline: ${(props: StyledRadioProps): string => (props.assistAccess ? '1px dotted #212121' : '0')};
        outline: ${(props: StyledRadioProps): string =>
            props.assistAccess ? '5px auto -webkit-focus-ring-color' : '0'};
    }

    &:checked {
        ~ ${StyledAscIcon} .sort-number-asc-icon__background,
        ~ ${StyledDescIcon} .sort-number-desc-icon__background {
            fill: ${(props: ThemeProps<SortFieldTheme>): string => props.theme.selectedColor};
        }
        ~ ${StyledAscIcon} .sort-number-asc-icon__outline,
        ~ ${StyledDescIcon} .sort-number-desc-icon__outline {
            fill: none;
        }
        ~ ${StyledAscIcon} .sort-number-asc-icon__1,
        ~ ${StyledAscIcon} .sort-number-asc-icon__9,
        ~ ${StyledAscIcon} .sort-number-asc-icon__arrow,
        ~ ${StyledDescIcon} .sort-number-desc-icon__1,
        ~ ${StyledDescIcon} .sort-number-desc-icon__9,
        ~ ${StyledDescIcon} .sort-number-desc-icon__arrow {
            fill: #fff;
        }
    }
`;

export const defaultTheme: SortFieldTheme = {
    selectedColor: COLOR.BRAND.REPORTING,
};

export const SortNumberAscDescField = React.forwardRef<HTMLInputElement, SortNumberAscDescFieldProps>(
    (
        {
            assistAccess = false,
            autoFocus,
            dataId,
            disabled,
            hasHelper,
            helperContent,
            id,
            invalid,
            legend,
            name,
            onChange,
            theme = defaultTheme,
            thinLabel,
            thinLegend,
            value,
            className,
            ...props
        },
        ref,
    ) => {
        return hasHelper ? (
            <ThemeProvider theme={theme}>
                <WrapperHasHelper data-test-id={`${dataId}`} className={className} {...props}>
                    <StyledLegend aria-label={`${id}-legend`}>
                        <StyledLegendText disabled={disabled} invalid={invalid} thinLegend={thinLegend}>
                            {legend}
                        </StyledLegendText>
                        <HelperModal dataId={dataId} modalContent={helperContent}>
                            <QuestionMark />
                        </HelperModal>
                    </StyledLegend>
                    <StyledItemsWrapper>
                        <StyledLabel inline disabled={disabled}>
                            <StyledLabelText
                                aria-label={`${id}-asc-text`}
                                disabled={disabled}
                                inline
                                thinLabel={thinLabel}
                            >
                                Ascending
                            </StyledLabelText>
                            <StyledRadio
                                aria-label={`${id}-asc-input`}
                                assistAccess={assistAccess}
                                autoFocus={autoFocus}
                                data-test-id={`${dataId}-asc-input`}
                                {...(typeof value !== 'undefined' && { checked: value === SortValues.ASCENDING })}
                                disabled={disabled}
                                id={`${id}-asc-input`}
                                name={name}
                                onChange={onChange}
                                ref={ref}
                                type="radio"
                                value={SortValues.ASCENDING}
                            />
                            <StyledAscIcon />
                        </StyledLabel>
                        <StyledLabel inline disabled={disabled}>
                            <StyledLabelText
                                aria-label={`${id}-desc-text`}
                                disabled={disabled}
                                inline
                                thinLabel={thinLabel}
                            >
                                Descending
                            </StyledLabelText>
                            <StyledRadio
                                aria-label={`${id}-desc-input`}
                                assistAccess={assistAccess}
                                autoFocus={autoFocus}
                                data-test-id={`${dataId}-desc-input`}
                                {...(typeof value !== 'undefined' && { checked: value === SortValues.DESCENDING })}
                                disabled={disabled}
                                id={`${id}-desc-input`}
                                name={name}
                                onChange={onChange}
                                ref={ref}
                                type="radio"
                                value={SortValues.DESCENDING}
                            />
                            <StyledDescIcon />
                        </StyledLabel>
                    </StyledItemsWrapper>
                </WrapperHasHelper>
            </ThemeProvider>
        ) : (
            <ThemeProvider theme={theme}>
                <Wrapper data-test-id={`${dataId}`} className={className} {...props}>
                    <StyledLegend aria-label={`${id}-legend`}>
                        <StyledLegendText disabled={disabled} invalid={invalid} thinLegend={thinLegend}>
                            {legend}
                        </StyledLegendText>
                    </StyledLegend>
                    <StyledItemsWrapper>
                        <StyledLabel inline disabled={disabled}>
                            <StyledLabelText
                                aria-label={`${id}-asc-text`}
                                disabled={disabled}
                                inline
                                thinLabel={thinLabel}
                            >
                                Ascending
                            </StyledLabelText>
                            <StyledRadio
                                aria-label={`${id}-asc-input`}
                                assistAccess={assistAccess}
                                autoFocus={autoFocus}
                                data-test-id={`${dataId}-asc-input`}
                                {...(typeof value !== 'undefined' && { checked: value === SortValues.ASCENDING })}
                                disabled={disabled}
                                id={`${id}-asc-input`}
                                name={name}
                                onChange={onChange}
                                ref={ref}
                                type="radio"
                                value={SortValues.ASCENDING}
                            />
                            <StyledAscIcon />
                        </StyledLabel>
                        <StyledLabel inline disabled={disabled}>
                            <StyledLabelText
                                aria-label={`${id}-desc-text`}
                                disabled={disabled}
                                inline
                                thinLabel={thinLabel}
                            >
                                Descending
                            </StyledLabelText>
                            <StyledRadio
                                aria-label={`${id}-desc-input`}
                                assistAccess={assistAccess}
                                autoFocus={autoFocus}
                                data-test-id={`${dataId}-desc-input`}
                                {...(typeof value !== 'undefined' && { checked: value === SortValues.DESCENDING })}
                                disabled={disabled}
                                id={`${id}-desc-input`}
                                name={name}
                                onChange={onChange}
                                ref={ref}
                                type="radio"
                                value={SortValues.DESCENDING}
                            />
                            <StyledDescIcon />
                        </StyledLabel>
                    </StyledItemsWrapper>
                </Wrapper>
            </ThemeProvider>
        );
    },
);
SortNumberAscDescField.displayName = 'SortNumberAscDescField';
